import { Head, Link } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Shuffle } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { RestartButton, StudySession } from "@/components/flashcards/StudySession"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { DeckSummary, StudyCard } from "@/types/flashcards"

export default function DeckStudy({ deck, cards, today }: { deck: DeckSummary; cards: StudyCard[]; today: string }) {
  const intl = useIntl()
  const format = useFormat()
  const description = intl.formatMessage({ id: "flashcards.study_meta" }, { cards: format.cardCount(cards.length), title: deck.title })
  const title = intl.formatMessage({ id: "flashcards.study_title" }, { title: deck.title })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href={`/decks/${deck.id}`} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {deck.title}
        </Link>

        <div className="mx-auto max-w-2xl">
          {cards.length === 0 ? (
            <div className="callout mt-6">
              <p>{intl.formatMessage({ id: "flashcards.no_cards" })}</p>
            </div>
          ) : (
            <StudySession
              cards={cards}
              today={today}
              header={({ shuffleRemaining }) => (
                <div className="flex items-center justify-between gap-4">
                  <h1>{deck.title}</h1>
                  <Button type="button" variant="ghost" size="sm" onClick={shuffleRemaining}>
                    <Shuffle className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.shuffle" })}
                  </Button>
                </div>
              )}
              doneActions={(restart) => (
                <>
                  <RestartButton onClick={restart} />
                  <Button asChild>
                    <Link href={`/decks/${deck.id}`}>{intl.formatMessage({ id: "flashcards.back_to_deck" })}</Link>
                  </Button>
                </>
              )}
            />
          )}
        </div>
      </AppShell>
    </>
  )
}
